import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Drawer,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import Delete from '@mui/icons-material/Delete';
import CreditCard from '@mui/icons-material/CreditCard';
import Payments from '@mui/icons-material/Payments';
import Bluetooth from '@mui/icons-material/Bluetooth';
import Wifi from '@mui/icons-material/Wifi';
import BatteryAlert from '@mui/icons-material/BatteryAlert';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Logout from '@mui/icons-material/Logout';
import LocalFireDepartment from '@mui/icons-material/LocalFireDepartment';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import Fastfood from '@mui/icons-material/Fastfood';
import LocalCafe from '@mui/icons-material/LocalCafe';
import Icecream from '@mui/icons-material/Icecream';
import LocalPizza from '@mui/icons-material/LocalPizza';
import LunchDining from '@mui/icons-material/LunchDining';
import LocalDrink from '@mui/icons-material/LocalDrink';
import EmojiFoodBeverage from '@mui/icons-material/EmojiFoodBeverage';
import Cookie from '@mui/icons-material/Cookie';
import SportsBar from '@mui/icons-material/SportsBar';
import Coffee from '@mui/icons-material/Coffee';
import BakeryDining from '@mui/icons-material/BakeryDining';
import RamenDining from '@mui/icons-material/RamenDining';
import Egg from '@mui/icons-material/Egg';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Worker, Fee, CardReader, PaymentMethod, POSTransactionItem } from '../../types';
import { useSchoolSettings } from '../../contexts/SchoolSettingsContext';
import { mockFees } from '../../mock/fees';
import { mockCardReaders } from '../../mock/workers';

interface POSInterfaceProps {
  worker: Worker;
  onLogout: () => void;
}

interface CartItem {
  feeId: string;
  fee: Fee;
  quantity: number;
}

interface GroupedTicketEvent {
  baseId: string;
  eventName: string;
  description: string;
  tickets: {
    type: 'student' | 'adult' | 'senior';
    label: string;
    fee: Fee;
  }[];
}

const getItemIcon = (fee: Fee) => {
  if (fee.icon) {
    switch (fee.icon) {
      case 'fastfood': return <Fastfood />;
      case 'lunch': return <LunchDining />;
      case 'pizza': return <LocalPizza />;
      case 'drink': return <LocalDrink />;
      case 'coffee': return <Coffee />;
      case 'beverage': return <EmojiFoodBeverage />;
      case 'icecream': return <Icecream />;
      case 'cafe': return <LocalCafe />;
      case 'cookie': return <Cookie />;
      case 'beer': return <SportsBar />;
      case 'bakery': return <BakeryDining />;
      case 'ramen': return <RamenDining />;
      case 'egg': return <Egg />;
      default: return <LocalCafe />;
    }
  }

  const nameLower = fee.name.toLowerCase();
  if (nameLower.includes('hot dog') || nameLower.includes('burger')) return <LunchDining />;
  if (nameLower.includes('pizza')) return <LocalPizza />;
  if (nameLower.includes('soda') || nameLower.includes('drink') || nameLower.includes('water') || nameLower.includes('gatorade')) return <LocalDrink />;
  if (nameLower.includes('coffee') || nameLower.includes('chocolate')) return <EmojiFoodBeverage />;
  if (nameLower.includes('ice cream') || nameLower.includes('cotton candy')) return <Icecream />;
  if (nameLower.includes('popcorn') || nameLower.includes('nachos') || nameLower.includes('pretzel') || nameLower.includes('fries')) return <Fastfood />;
  return <LocalCafe />;
};

export const POSInterface: React.FC<POSInterfaceProps> = ({ worker, onLogout }) => {
  const { settings } = useSchoolSettings();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedReader, setSelectedReader] = useState<CardReader | null>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [cashTendered, setCashTendered] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [transactionComplete, setTransactionComplete] = useState(false);
  const [receiptData, setReceiptData] = useState<{
    items: POSTransactionItem[];
    subtotal: number;
    tax: number;
    total: number;
    paymentMethod: PaymentMethod;
    change?: number;
    transactionId: string;
  } | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [eventSelectorOpen, setEventSelectorOpen] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [activeMode, setActiveMode] = useState<'concession' | 'ticket'>('concession');

  const isConcessionWorker = worker.workerTypes.includes('concession');
  const isTicketWorker = worker.workerTypes.includes('ticket');

  const availableFees = useMemo(() => {
    let fees: Fee[] = [];

    if (activeMode === 'concession') {
      fees = mockFees
        .filter(f => f.isActive && f.categoryId === '7' && f.feeType === 'concession')
        .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0));
    } else if (activeMode === 'ticket') {
      fees = mockFees
        .filter(f => f.isActive && f.feeType === 'event_ticket')
        .sort((a, b) => (b.popularityScore || 0) - (a.popularityScore || 0));
    }

    if (itemSearchQuery) {
      fees = fees.filter(f =>
        f.name.toLowerCase().includes(itemSearchQuery.toLowerCase())
      );
    }

    return fees;
  }, [activeMode, itemSearchQuery]);

  const groupedTicketEvents = useMemo(() => {
    if (activeMode !== 'ticket') return [];

    const ticketFees = mockFees.filter(f => f.isActive && f.feeType === 'event_ticket');
    const eventMap = new Map<string, GroupedTicketEvent>();

    ticketFees.forEach(fee => {
      const baseId = fee.id.replace(/-student$|-adult$|-senior$/, '');
      let ticketType: 'student' | 'adult' | 'senior' = 'adult';
      let label = 'Adult';

      if (fee.id.endsWith('-student')) {
        ticketType = 'student';
        label = 'Student';
      } else if (fee.id.endsWith('-senior')) {
        ticketType = 'senior';
        label = 'Senior';
      }

      const eventName = fee.name.replace(/ - (Student\/Child|Adult|Senior)$/, '');

      if (!eventMap.has(baseId)) {
        eventMap.set(baseId, {
          baseId,
          eventName,
          description: fee.description || '',
          tickets: [],
        });
      }

      eventMap.get(baseId)!.tickets.push({
        type: ticketType,
        label,
        fee,
      });
    });

    let events = Array.from(eventMap.values());
    events.forEach(event => {
      event.tickets.sort((a, b) => {
        const order = { student: 0, adult: 1, senior: 2 };
        return order[a.type] - order[b.type];
      });
    });

    if (itemSearchQuery) {
      events = events.filter(e =>
        e.eventName.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
        e.description.toLowerCase().includes(itemSearchQuery.toLowerCase())
      );
    }

    return events;
  }, [activeMode, itemSearchQuery]);

  const availableEvents = useMemo(() => {
    const events = mockFees.filter(
      f => f.isActive && f.categoryId === '5' && f.eventPricing
    );
    if (eventSearchQuery) {
      return events.filter(e =>
        e.name.toLowerCase().includes(eventSearchQuery.toLowerCase()) ||
        e.description?.toLowerCase().includes(eventSearchQuery.toLowerCase())
      );
    }
    return events;
  }, [eventSearchQuery]);

  useEffect(() => {
    if (worker.assignedEventIds && worker.assignedEventIds.length > 0) {
      setSelectedEvents(worker.assignedEventIds);
    }
  }, [worker.assignedEventIds]);

  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const price = item.fee.basePrice;
      return sum + price * item.quantity;
    }, 0);
  }, [cart]);

  // Tax only applies to items flagged taxable on the fee record
  // (concession items, merch). Educational fees stay tax-exempt.
  // Rate is configured per-school via SchoolSettings.taxRate.
  const taxableSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      if (!item.fee.taxable) return sum;
      return sum + item.fee.basePrice * item.quantity;
    }, 0);
  }, [cart]);

  const tax = taxableSubtotal * settings.taxRate;
  const total = subtotal + tax;
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleAddItem = (fee: Fee) => {
    setCart(prev => {
      const existing = prev.find(item => item.feeId === fee.id);
      if (existing) {
        return prev.map(item =>
          item.feeId === fee.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { feeId: fee.id, fee, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (feeId: string, delta: number) => {
    setCart(prev =>
      prev
        .map(item => {
          if (item.feeId === feeId) {
            const newQty = item.quantity + delta;
            return newQty > 0 ? { ...item, quantity: newQty } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  };

  const handleRemoveItem = (feeId: string) => {
    setCart(prev => prev.filter(item => item.feeId !== feeId));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const handleOpenPayment = () => {
    if (cart.length === 0) return;
    setMobileCartOpen(false);
    setPaymentDialogOpen(true);
    setPaymentMethod('card');
    setCashTendered('');
  };

  const handleProcessPayment = async () => {
    setProcessing(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    const transactionId = `TXN-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

    const change = paymentMethod === 'cash' && cashTendered
      ? parseFloat(cashTendered) - total
      : undefined;

    setReceiptData({
      items: cart.map(item => ({
        feeId: item.feeId,
        feeName: item.fee.name,
        quantity: item.quantity,
        unitPrice: item.fee.basePrice,
        totalPrice: item.fee.basePrice * item.quantity,
      })),
      subtotal,
      tax,
      total,
      paymentMethod,
      change,
      transactionId,
    });

    setProcessing(false);
    setTransactionComplete(true);
  };

  const handleNewTransaction = () => {
    setCart([]);
    setPaymentDialogOpen(false);
    setTransactionComplete(false);
    setReceiptData(null);
    setCashTendered('');
  };

  // Tour-driven concession demo (step 15). The cursor "clicks" pizza
  // and water tiles, then the Charge → Process Payment buttons; these
  // triggers do the actual state mutations since the cursor click is
  // visual-only. Cleanup (clear cart, close dialog) only on actual
  // step change — detected via stepId.
  // Refs because the trigger-listener useEffect runs once with empty
  // deps; without these, handleOpenPayment's `cart.length === 0` and
  // handleProcessPayment's read of cart/total/etc. would all use the
  // initial-render closures (empty cart, zero total).
  const cartRef = useRef(cart);
  const fnRef = useRef({ handleAddItem, handleProcessPayment });
  useEffect(() => { cartRef.current = cart; }, [cart]);
  useEffect(() => { fnRef.current = { handleAddItem, handleProcessPayment }; });
  useEffect(() => {
    let ownerStepId: string | null = null;
    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const stepId: string | undefined = detail?.stepId;
      const trigger = detail?.trigger;
      if (trigger === 'pos-add-pizza-slice') {
        ownerStepId = stepId ?? null;
        const pizza = mockFees.find((f) => f.id === 'concession-008');
        if (pizza) fnRef.current.handleAddItem(pizza);
      } else if (trigger === 'pos-add-bottled-water') {
        ownerStepId = stepId ?? null;
        const water = mockFees.find((f) => f.id === 'concession-013');
        if (water) fnRef.current.handleAddItem(water);
      } else if (trigger === 'pos-open-payment') {
        ownerStepId = stepId ?? null;
        if (cartRef.current.length > 0) {
          setMobileCartOpen(false);
          setPaymentDialogOpen(true);
          setPaymentMethod('card');
          setCashTendered('');
        }
      } else if (trigger === 'pos-process-payment') {
        ownerStepId = stepId ?? null;
        fnRef.current.handleProcessPayment();
      } else if (ownerStepId !== null && stepId !== ownerStepId) {
        // Step actually moved on — reset for the next visit.
        ownerStepId = null;
        setPaymentDialogOpen(false);
        setTransactionComplete(false);
        setReceiptData(null);
        setCart([]);
      }
    };
    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => window.removeEventListener('cc-tour-trigger', onTrigger);
  }, []);

  const connectedReaders = mockCardReaders.filter(r => r.status === 'connected');

  useEffect(() => {
    if (connectedReaders.length > 0 && !selectedReader) {
      setSelectedReader(connectedReaders[0]);
    }
  }, [connectedReaders, selectedReader]);

  const cashChangeAmount = paymentMethod === 'cash' && cashTendered
    ? parseFloat(cashTendered) - total
    : 0;

  const CartContent = () => (
    <>
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {cart.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 150,
              opacity: 0.5,
            }}
          >
            <Typography variant="body1">No items in cart</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Tap items to add them
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {cart.map(item => {
              const price = item.fee.basePrice;
              return (
                <Paper
                  key={item.feeId}
                  sx={{
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {item.fee.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveItem(item.feeId)}
                      sx={{ color: '#ef4444', p: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateQuantity(item.feeId, -1)}
                        sx={{
                          bgcolor: 'grey.300',
                          color: 'text.primary',
                          width: 32,
                          height: 32,
                          '&:hover': { bgcolor: 'grey.400' },
                        }}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography variant="body1" fontWeight={600} sx={{ minWidth: 24, textAlign: 'center' }} color="text.primary">
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleUpdateQuantity(item.feeId, 1)}
                        sx={{
                          bgcolor: 'grey.300',
                          color: 'text.primary',
                          width: 32,
                          height: 32,
                          '&:hover': { bgcolor: 'grey.400' },
                        }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      ${(price * item.quantity).toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body2" color="text.primary">${subtotal.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Tax (7%)</Typography>
            <Typography variant="body2" color="text.primary">${tax.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: 1, borderColor: '#e5e7eb' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700}>Total</Typography>
            <Typography variant="h6" fontWeight={700} color="primary">
              ${total.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleClearCart}
            disabled={cart.length === 0}
          >
            Clear
          </Button>
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleOpenPayment}
          disabled={cart.length === 0}
          sx={{
            height: 56,
            fontSize: '1.1rem',
            fontWeight: 700,
            bgcolor: '#22c55e',
            '&:hover': { bgcolor: '#16a34a' },
            '&:disabled': { bgcolor: '#d1d5db', color: '#9ca3af' },
          }}
        >
          Charge ${total.toFixed(2)}
        </Button>
      </Box>
    </>
  );

  return (
    <Box data-tour="pos-terminal" sx={{ display: 'flex', height: '100vh', bgcolor: '#1e293b', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f8fafc',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: 2,
                  bgcolor: activeMode === 'concession' ? '#f59e0b' : '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {activeMode === 'concession' ? (
                  <Fastfood sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                ) : (
                  <ConfirmationNumber sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                )}
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                  {activeMode === 'concession' ? 'Concessions' : 'Tickets'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {worker.firstName} {worker.lastName}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activeMode === 'ticket' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ConfirmationNumber />}
                  onClick={() => setEventSelectorOpen(true)}
                  sx={{ borderRadius: 2, display: { xs: 'none', sm: 'flex' } }}
                >
                  Events ({selectedEvents.length})
                </Button>
              )}
              {activeMode === 'ticket' && (
                <IconButton
                  onClick={() => setEventSelectorOpen(true)}
                  sx={{ display: { xs: 'flex', sm: 'none' }, bgcolor: 'primary.50' }}
                >
                  <ConfirmationNumber color="primary" />
                </IconButton>
              )}

              <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 180 }, display: { xs: 'none', md: 'flex' } }}>
                <InputLabel>Card Reader</InputLabel>
                <Select
                  value={selectedReader?.id || ''}
                  onChange={(e) => {
                    const reader = mockCardReaders.find(r => r.id === e.target.value);
                    setSelectedReader(reader || null);
                  }}
                  label="Card Reader"
                >
                  {mockCardReaders.map(reader => (
                    <MenuItem key={reader.id} value={reader.id} disabled={reader.status !== 'connected'}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {reader.type === 'bluetooth' ? (
                          <Bluetooth fontSize="small" color={reader.status === 'connected' ? 'primary' : 'disabled'} />
                        ) : (
                          <Wifi fontSize="small" color={reader.status === 'connected' ? 'primary' : 'disabled'} />
                        )}
                        <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                          {reader.name.split(' - ')[1] || reader.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <IconButton
                onClick={onLogout}
                size="small"
                sx={{
                  bgcolor: '#fee2e2',
                  color: '#dc2626',
                  '&:hover': { bgcolor: '#fecaca' },
                }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              pb: { xs: 1.5, sm: 2 },
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                bgcolor: '#f1f5f9',
                borderRadius: 2,
                p: 0.5,
                flex: { xs: 'none', sm: 'none' },
              }}
            >
              <Button
                size="small"
                onClick={() => setActiveMode('concession')}
                startIcon={<Fastfood />}
                sx={{
                  borderRadius: 1.5,
                  px: 2,
                  bgcolor: activeMode === 'concession' ? 'white' : 'transparent',
                  color: activeMode === 'concession' ? '#f59e0b' : 'text.secondary',
                  boxShadow: activeMode === 'concession' ? 1 : 0,
                  fontWeight: activeMode === 'concession' ? 600 : 400,
                  '&:hover': {
                    bgcolor: activeMode === 'concession' ? 'white' : '#e2e8f0',
                  },
                }}
              >
                Concessions
              </Button>
              <Button
                size="small"
                onClick={() => setActiveMode('ticket')}
                startIcon={<ConfirmationNumber />}
                sx={{
                  borderRadius: 1.5,
                  px: 2,
                  bgcolor: activeMode === 'ticket' ? 'white' : 'transparent',
                  color: activeMode === 'ticket' ? '#3b82f6' : 'text.secondary',
                  boxShadow: activeMode === 'ticket' ? 1 : 0,
                  fontWeight: activeMode === 'ticket' ? 600 : 400,
                  '&:hover': {
                    bgcolor: activeMode === 'ticket' ? 'white' : '#e2e8f0',
                  },
                }}
              >
                Tickets
              </Button>
            </Box>

            <TextField
              placeholder={activeMode === 'concession' ? 'Search items...' : 'Search tickets...'}
              value={itemSearchQuery}
              onChange={(e) => setItemSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: itemSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setItemSearchQuery('')}>
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: { xs: 1, sm: 2 }, overflow: 'auto', pb: cart.length > 0 ? '180px' : '120px' }}>
          {activeMode === 'concession' && availableFees.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(3, 1fr)',
                  sm: 'repeat(auto-fill, minmax(130px, 1fr))',
                },
                gap: { xs: 1, sm: 1.5 },
              }}
            >
              {availableFees.map((fee) => {
                const cartItem = cart.find(item => item.feeId === fee.id);
                const price = fee.basePrice;
                const isPopular = (fee.popularityScore || 0) >= 85;

                return (
                  <Paper
                    key={fee.id}
                    sx={{
                      p: { xs: 1, sm: 2 },
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      position: 'relative',
                      borderRadius: 2,
                      border: cartItem ? '2px solid #3b82f6' : '2px solid transparent',
                      bgcolor: cartItem ? '#eff6ff' : 'white',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    {isPopular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                        }}
                      >
                        <LocalFireDepartment sx={{ color: '#f59e0b', fontSize: { xs: 16, sm: 20 } }} />
                      </Box>
                    )}

                    {cartItem && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateQuantity(fee.id, -1);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          bgcolor: '#ef4444',
                          color: 'white',
                          width: { xs: 24, sm: 28 },
                          height: { xs: 24, sm: 28 },
                          '&:hover': { bgcolor: '#dc2626' },
                          zIndex: 2,
                        }}
                      >
                        <Remove sx={{ fontSize: { xs: 14, sm: 16 } }} />
                      </IconButton>
                    )}

                    <Box
                      onClick={() => handleAddItem(fee)}
                      {...(fee.id === 'concession-008'
                        ? { 'data-tour': 'pos-item-pizza-slice' }
                        : fee.id === 'concession-013'
                          ? { 'data-tour': 'pos-item-bottled-water' }
                          : {})}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 36, sm: 48 },
                          height: { xs: 36, sm: 48 },
                          borderRadius: 2,
                          bgcolor: '#fef3c7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: { xs: 0.5, sm: 1 },
                          color: '#f59e0b',
                          '& svg': { fontSize: { xs: 20, sm: 24 } },
                          position: 'relative',
                        }}
                      >
                        {getItemIcon(fee)}
                        {cartItem && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              bgcolor: '#3b82f6',
                              color: 'white',
                              borderRadius: '50%',
                              width: { xs: 20, sm: 24 },
                              height: { xs: 20, sm: 24 },
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              fontWeight: 700,
                            }}
                          >
                            {cartItem.quantity}
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          lineHeight: 1.2,
                          minHeight: { xs: 28, sm: 36 },
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        }}
                      >
                        {fee.name}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                        ${price.toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : activeMode === 'ticket' && groupedTicketEvents.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {groupedTicketEvents.map((event) => (
                <Paper
                  key={event.baseId}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: '#dbeafe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#3b82f6',
                        flexShrink: 0,
                      }}
                    >
                      <ConfirmationNumber />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                        {event.eventName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {event.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)' },
                      gap: 1,
                    }}
                  >
                    {event.tickets.map((ticket) => {
                      const cartItem = cart.find(item => item.feeId === ticket.fee.id);
                      const typeColors = {
                        student: { bg: '#fef3c7', border: '#f59e0b', text: '#b45309' },
                        adult: { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8' },
                        senior: { bg: '#dcfce7', border: '#22c55e', text: '#15803d' },
                      };
                      const colors = typeColors[ticket.type];

                      return (
                        <Box
                          key={ticket.fee.id}
                          sx={{
                            position: 'relative',
                            bgcolor: cartItem ? colors.bg : '#f8fafc',
                            border: '2px solid',
                            borderColor: cartItem ? colors.border : 'transparent',
                            borderRadius: 2,
                            p: 1.5,
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: colors.bg,
                              borderColor: colors.border,
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            },
                          }}
                          onClick={() => handleAddItem(ticket.fee)}
                        >
                          {cartItem && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateQuantity(ticket.fee.id, -1);
                              }}
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                bgcolor: '#ef4444',
                                color: 'white',
                                width: 24,
                                height: 24,
                                '&:hover': { bgcolor: '#dc2626' },
                                zIndex: 2,
                              }}
                            >
                              <Remove sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                              color: cartItem ? colors.text : 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}
                          >
                            {ticket.label}
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ color: cartItem ? colors.text : 'text.primary', mt: 0.5 }}
                          >
                            ${ticket.fee.basePrice.toFixed(2)}
                          </Typography>
                          {cartItem && (
                            <Badge
                              badgeContent={cartItem.quantity}
                              color="primary"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                '& .MuiBadge-badge': {
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  minWidth: 20,
                                  height: 20,
                                },
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              {activeMode === 'ticket' && itemSearchQuery ? (
                <>
                  <Search sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No Tickets Found</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Try a different search term
                  </Typography>
                </>
              ) : activeMode === 'ticket' ? (
                <>
                  <ConfirmationNumber sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No Tickets Available</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    No event tickets are currently active
                  </Typography>
                </>
              ) : itemSearchQuery ? (
                <>
                  <Search sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No Results Found</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Try a different search term
                  </Typography>
                </>
              ) : (
                <>
                  <Fastfood sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">No Items Available</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: '#0f172a',
          color: 'white',
          p: 2,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.2)',
          zIndex: 10,
        }}
      >
        <Box
          onClick={() => setMobileCartOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            mb: cart.length > 0 ? 2 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Badge badgeContent={cartItemCount} color="primary">
              <ShoppingCart />
            </Badge>
            <Box>
              <Typography variant="body2" color="grey.400">
                {cartItemCount} {cartItemCount === 1 ? 'item' : 'items'}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                ${total.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="grey.400">View Order</Typography>
            <KeyboardArrowUp />
          </Box>
        </Box>

        {cart.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                handleClearCart();
              }}
              sx={{
                flex: '0 0 80px',
                borderColor: '#475569',
                color: 'white',
                '&:hover': { borderColor: '#64748b', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPayment();
              }}
              data-tour="pos-charge-btn"
              sx={{
                height: 48,
                fontSize: '1rem',
                fontWeight: 700,
                bgcolor: '#22c55e',
                '&:hover': { bgcolor: '#16a34a' },
              }}
            >
              Charge ${total.toFixed(2)}
            </Button>
          </Box>
        )}
      </Box>

          <Drawer
            anchor="bottom"
            open={mobileCartOpen}
            onClose={() => setMobileCartOpen(false)}
            PaperProps={{
              sx: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                maxHeight: '85vh',
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={700}>Current Order</Typography>
              <IconButton onClick={() => setMobileCartOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <CartContent />
            </Box>
          </Drawer>

      <Dialog
        open={paymentDialogOpen}
        onClose={() => !processing && !transactionComplete && setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {!transactionComplete ? (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Payment
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total: ${total.toFixed(2)}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {processing ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <LinearProgress sx={{ mb: 3 }} />
                  <Typography variant="h6" fontWeight={600}>
                    Processing Payment...
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {paymentMethod === 'card'
                      ? 'Waiting for card reader...'
                      : 'Recording cash transaction...'}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    Payment Method
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Paper
                      onClick={() => setPaymentMethod('card')}
                      sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: paymentMethod === 'card' ? 'primary.main' : '#e5e7eb',
                        bgcolor: paymentMethod === 'card' ? 'primary.50' : 'white',
                        borderRadius: 2,
                        textAlign: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <CreditCard
                        sx={{
                          fontSize: 40,
                          color: paymentMethod === 'card' ? 'primary.main' : 'grey.500',
                        }}
                      />
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                        Card
                      </Typography>
                    </Paper>
                    <Paper
                      onClick={() => setPaymentMethod('cash')}
                      sx={{
                        flex: 1,
                        p: 2,
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: paymentMethod === 'cash' ? 'success.main' : '#e5e7eb',
                        bgcolor: paymentMethod === 'cash' ? 'success.50' : 'white',
                        borderRadius: 2,
                        textAlign: 'center',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <Payments
                        sx={{
                          fontSize: 40,
                          color: paymentMethod === 'cash' ? 'success.main' : 'grey.500',
                        }}
                      />
                      <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                        Cash
                      </Typography>
                    </Paper>
                  </Box>

                  {paymentMethod === 'card' && (
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                        Selected Card Reader
                      </Typography>
                      {selectedReader ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {selectedReader.type === 'bluetooth' ? (
                            <Bluetooth color="primary" />
                          ) : (
                            <Wifi color="primary" />
                          )}
                          <Typography variant="body2">{selectedReader.name}</Typography>
                          <Chip
                            label="Connected"
                            size="small"
                            color="success"
                            sx={{ ml: 'auto' }}
                          />
                        </Box>
                      ) : (
                        <Alert severity="warning">
                          No card reader connected. Please select a reader from the header.
                        </Alert>
                      )}
                    </Box>
                  )}

                  {paymentMethod === 'cash' && (
                    <Box>
                      <TextField
                        label="Cash Tendered"
                        type="number"
                        value={cashTendered}
                        onChange={(e) => setCashTendered(e.target.value)}
                        fullWidth
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Quick Tender
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {[5, 10, 20, 50, 100].map((amount) => (
                            <Button
                              key={amount}
                              variant={parseFloat(cashTendered) === amount ? 'contained' : 'outlined'}
                              onClick={() => setCashTendered(amount.toString())}
                              sx={{
                                minWidth: 64,
                                fontWeight: 600,
                                bgcolor: parseFloat(cashTendered) === amount ? '#22c55e' : undefined,
                                borderColor: '#22c55e',
                                color: parseFloat(cashTendered) === amount ? 'white' : '#22c55e',
                                '&:hover': {
                                  bgcolor: parseFloat(cashTendered) === amount ? '#16a34a' : 'rgba(34, 197, 94, 0.08)',
                                  borderColor: '#16a34a',
                                },
                              }}
                            >
                              ${amount}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                      {parseFloat(cashTendered) >= total && (
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: '#dcfce7',
                            borderRadius: 2,
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="body2" color="success.dark" fontWeight={600}>
                            Change Due
                          </Typography>
                          <Typography variant="h4" fontWeight={700} color="success.dark">
                            ${cashChangeAmount.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      {parseFloat(cashTendered) > 0 && parseFloat(cashTendered) < total && (
                        <Alert severity="error">
                          Insufficient cash. Need ${(total - parseFloat(cashTendered)).toFixed(2)} more.
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setPaymentDialogOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleProcessPayment}
                disabled={
                  processing ||
                  (paymentMethod === 'card' && !selectedReader) ||
                  (paymentMethod === 'cash' && (!cashTendered || parseFloat(cashTendered) < total))
                }
                data-tour="pos-process-payment-btn"
                sx={{
                  bgcolor: '#22c55e',
                  '&:hover': { bgcolor: '#16a34a' },
                  px: 4,
                }}
              >
                Process Payment
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent sx={{ py: 4, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Payment Successful!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Transaction ID: {receiptData?.transactionId}
              </Typography>

              <Paper sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, textAlign: 'left', mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Receipt
                </Typography>
                {receiptData?.items.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">
                      {item.quantity}x {item.feeName}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      ${item.totalPrice.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">${receiptData?.subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Tax</Typography>
                  <Typography variant="body2">${receiptData?.tax.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body1" fontWeight={700}>Total</Typography>
                  <Typography variant="body1" fontWeight={700}>${receiptData?.total.toFixed(2)}</Typography>
                </Box>
                {receiptData?.paymentMethod === 'cash' && receiptData?.change !== undefined && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, p: 1, bgcolor: '#dcfce7', borderRadius: 1 }}>
                    <Typography variant="body1" fontWeight={600} color="success.dark">Change</Typography>
                    <Typography variant="body1" fontWeight={700} color="success.dark">
                      ${receiptData.change.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Chip
                label={receiptData?.paymentMethod === 'card' ? 'Paid with Card' : 'Paid with Cash'}
                icon={receiptData?.paymentMethod === 'card' ? <CreditCard /> : <Payments />}
                color={receiptData?.paymentMethod === 'card' ? 'primary' : 'success'}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleNewTransaction}
                sx={{
                  bgcolor: '#22c55e',
                  '&:hover': { bgcolor: '#16a34a' },
                }}
              >
                New Transaction
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={eventSelectorOpen}
        onClose={() => setEventSelectorOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Select Events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose which events you're selling tickets for
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            placeholder="Search events..."
            value={eventSearchQuery}
            onChange={(e) => setEventSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
            {availableEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography>No events found</Typography>
              </Box>
            ) : (
              availableEvents.map(event => (
                <Paper
                  key={event.id}
                  onClick={() => {
                    setSelectedEvents(prev =>
                      prev.includes(event.id)
                        ? prev.filter(id => id !== event.id)
                        : [...prev, event.id]
                    );
                  }}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: selectedEvents.includes(event.id) ? 'primary.main' : '#e5e7eb',
                    bgcolor: selectedEvents.includes(event.id) ? 'primary.50' : 'white',
                    borderRadius: 2,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={600}>
                        {event.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {event.description}
                      </Typography>
                      {event.eventPricing && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={`Student: $${event.eventPricing.studentChild.toFixed(2)}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Adult: $${event.eventPricing.adult.toFixed(2)}`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={`Senior: $${event.eventPricing.senior.toFixed(2)}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEventSelectorOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => setEventSelectorOpen(false)}
          >
            Apply ({selectedEvents.length} selected)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
